/**
 * Hold type filter logic for projects view.
 * Manages parent/child checkbox state and parameter building.
 */

export interface HoldTypeItem {
  value: string
  label: string
}

export const HOLD_TYPES: HoldTypeItem[] = [
  { value: 'needs_paint_sub', label: 'Needs Paint' },
  { value: 'customer_hold', label: 'Customer Hold' },
  { value: 'other', label: 'Other' },
]

/**
 * Determine the parent "On Hold" checkbox state given a set of selected hold types.
 * @param selectedHoldTypes Set of selected hold type values
 * @returns 'all' if all hold types selected, 'some' if some selected, 'none' if none selected
 */
export function getParentHoldCheckState(selectedHoldTypes: Set<string>): 'all' | 'some' | 'none' {
  if (selectedHoldTypes.size === 0) return 'none'
  if (selectedHoldTypes.size === HOLD_TYPES.length) return 'all'
  return 'some'
}

/**
 * Toggle a hold type child checkbox.
 * @param selectedHoldTypes Current set of selected hold types
 * @param holdTypeValue The hold type value to toggle
 * @returns New set with the toggled value
 */
export function toggleHoldType(
  selectedHoldTypes: Set<string>,
  holdTypeValue: string
): Set<string> {
  const next = new Set(selectedHoldTypes)
  if (next.has(holdTypeValue)) {
    next.delete(holdTypeValue)
  } else {
    next.add(holdTypeValue)
  }
  return next
}

/**
 * Toggle parent "On Hold" checkbox (selects/deselects all hold types).
 * @param selectedHoldTypes Current set of selected hold types
 * @param allSelected Whether all hold types should be selected
 * @returns New set with all hold types selected (if allSelected) or none (if not)
 */
export function toggleParentHold(
  selectedHoldTypes: Set<string>,
  allSelected: boolean
): Set<string> {
  if (allSelected) {
    // Deselect all
    return new Set<string>()
  } else {
    // Select all
    return new Set(HOLD_TYPES.map(ht => ht.value))
  }
}
